import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Globe, Package, Award, Building2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ProductSlider } from '../components/ProductSlider';
import { companyInfo, stats, products } from '../data/mock';

export const HomePage = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="relative overflow-hidden pt-32 pb-20 lg:pt-40 lg:pb-28 !bg-[#ECECEC]">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-orange-50/30 to-slate-100 -z-10"></div>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNiIgc3Ryb2tlPSIjMTkzYjYwIiBzdHJva2Utd2lkdGg9IjAuNSIgb3BhY2l0eT0iMC4xIi8+PC9nPjwvc3ZnPg==')] opacity-40 -z-10"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 text-#b83817 rounded-full text-sm font-semibold">
                <Globe size={16} />
                <span>Exporting to 5+ Countries</span>
              </div>

              <div className="space-y-4">
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight">
                  Premium PVC
                  <span className="block !text-[#D8461D]">Flexible Pipes</span>
                </h1>
                <p className="text-lg sm:text-xl text-slate-600 leading-relaxed max-w-xl">
                  {companyInfo.tagline}
                </p>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-3 text-slate-700">
                  <CheckCircle2 size={20} className="text-[#D8461D] flex-shrink-0" />
                  <span className="font-medium">{companyInfo.experience} of Manufacturing Excellence</span>
                </div>
                <div className="flex items-center gap-3 text-slate-700">
                  <CheckCircle2 size={20} className="text-[#D8461D] flex-shrink-0" />
                  <span className="font-medium">Exporting to {companyInfo.exports.slice(0, 3).join(', ')} and more</span>
                </div>
                <div className="flex items-center gap-3 text-slate-700">
                  <CheckCircle2 size={20} className="text-[#D8461D] flex-shrink-0" />
                  <span className="font-medium">ISO Certified Quality Standards</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Link to="/products">
                  <Button
                    size="lg"
                    className="inline-flex items-center justify-center whitespace-nowrap focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 h-10 hover:bg-#b83817 transition-all duration-200 hover:shadow-xl hover:scale-105 group text-lg font-semibold gap-2 px-8 py-6 rounded-md shadow text-white !bg-[#D8461D]">

                    View Products
                    <ArrowRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-2 border-[#D8461D] text-[#D8461D] hover:bg-orange-50 px-8 py-6 text-lg font-semibold transition-all duration-200">

                    Get Quote
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-500">
                <img
                  src="https://customer-assets.emergentagent.com/job_flex-line-1/artifacts/p4vy42gp_ref.webp"
                  alt="Colorful PVC Hoses"
                  className="w-full h-[500px] object-cover" />

                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent"></div>
              </div>

              <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 w-11/12 bg-white rounded-xl shadow-2xl p-6 border border-slate-200">
                <div className="grid grid-cols-4 gap-4">
                  {stats.map((stat, index) =>
                  <div key={index} className="text-center">
                      <div className="text-2xl sm:text-3xl font-bold !text-[#D8461D]">{stat.value}</div>
                      <div className="text-xs sm:text-sm text-slate-600 font-medium mt-1">{stat.label}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 space-y-4">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-4">
              <Package size={32} className="text-[#D8461D]" />
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900">
              Featured Products
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Premium quality hoses and pipes for industrial applications
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.slice(0, 4).map((product) =>
            <Link key={product.id} to={`/products/${product.id}`}>
                <Card className="group cursor-pointer transition-all duration-300 hover:shadow-xl border-slate-200 overflow-hidden h-full">
                  <div className="relative overflow-hidden bg-white">
                    <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-56 object-contain transition-transform duration-500 group-hover:scale-110 bg-white p-4" />

                  </div>
                  <CardContent className="p-6 space-y-3">
                    <h3 className="group-hover:text-[#D8461D] transition-colors text-xl font-bold !text-[#D8461D]">
                      {product.name}
                    </h3>
                    <p className="text-sm text-slate-600 leading-relaxed line-clamp-2">
                      {product.description}
                    </p>
                    <div className="group-hover:underline font-semibold text-sm !text-[#D8461D]">
                      Learn More →
                    </div>
                  </CardContent>
                </Card>
              </Link>
            )}
          </div>

          <div className="mt-12 text-center">
            <Link to="/products">
              <Button size="lg" className="inline-flex items-center justify-center whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 h-10 hover:bg-#b83817 text-sm font-medium gap-2 px-8 py-3 rounded-md shadow text-white !bg-[#D8461D]">
                View All Products
                <ArrowRight size={20} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Product Slider */}
      <ProductSlider />

      {/* Why Choose Us */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900">
              Why Choose Us
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Your trusted partner for industrial hose solutions
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="border-slate-200 bg-white hover:shadow-xl transition-shadow">
              <CardContent className="p-8 text-center space-y-4">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto">
                  <Award size={32} className="text-[#D8461D]" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900">Quality Certified</h3>
                <p className="text-slate-600">ISO certified manufacturing with rigorous quality standards</p>
              </CardContent>
            </Card>

            <Card className="border-slate-200 bg-white hover:shadow-xl transition-shadow">
              <CardContent className="p-8 text-center space-y-4">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto">
                  <Globe size={32} className="text-[#D8461D]" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900">Global Reach</h3>
                <p className="text-slate-600">Exporting to major international markets worldwide</p>
              </CardContent>
            </Card>

            <Card className="border-slate-200 bg-white hover:shadow-xl transition-shadow">
              <CardContent className="p-8 text-center space-y-4">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto">
                  <Building2 size={32} className="text-[#D8461D]" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900">Experience</h3>
                <p className="text-slate-600">Over 10 years of manufacturing excellence</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 !bg-[#D8461D]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8">
          <h2 className="text-4xl lg:text-5xl font-bold text-white">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-orange-100">
            Contact us today for custom solutions and bulk orders
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-[#D8461D] hover:bg-slate-100 px-8 py-6 text-lg font-semibold">
                Request Quote
                <ArrowRight size={20} className="ml-2" />
              </Button>
            </Link>
            <Link to="/products">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-#b83817 px-8 py-6 text-lg font-semibold">
                Browse Products
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>);

};

export default HomePage;